import React, { useMemo } from 'react';
import { Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Line, ComposedChart } from 'recharts';
import { Property } from '@/types/property';

// Mapeamento de cores
const TAILWIND_COLORS = {
  'blue-base': '#1e90ff',
  'blue-muted': '#4682b4',
  'blue-deep': '#191970',
  'blue-accent': '#00b7eb',
  'neutral-light': '#e6e9f0',
  'neutral-dark': '#2f4f4f',
  'highlight': '#ffa500',
};

interface NeighborhoodChartProps {
  properties: Property[];
  dashboardType?: 'venda' | 'aluguel' | 'sairam-ultima-semana' | 'entraram-ultima-semana';
}

interface NeighborhoodData {
  name: string;
  count: number;
  avgPricePerSqMeter: number;
}

const NeighborhoodChart: React.FC<NeighborhoodChartProps> = ({ 
  properties,
  dashboardType = 'venda'
}) => {
  const chartData = useMemo(() => {
    if (!properties || properties.length === 0) return [];

    const neighborhoodMap = new Map<string, { total: number; count: number; value: number }>();
    
    properties.forEach(property => {
      const neighborhood = property.bairro || 'Não informado';
      
      const price = typeof property.valor_recente === 'string'
        ? parseFloat(property.valor_recente.replace(/[^\d.-]/g, ''))
        : property.valor_recente;
      
      let area: number;
      if (typeof property.area_util === 'string') {
        area = parseFloat(property.area_util);
      } else {
        area = Number(property.area_util);
      }
      
      if (isNaN(area) || area <= 0) {
        if (typeof property.area_total === 'string') {
          area = parseFloat(property.area_total);
        } else {
          area = Number(property.area_total);
        }
      }
      
      if (!price || isNaN(area) || area <= 0) return;
      
      const pricePerSqm = price / area;
      
      if (neighborhoodMap.has(neighborhood)) {
        const current = neighborhoodMap.get(neighborhood)!;
        current.total += pricePerSqm;
        current.count += 1;
        current.value = current.total / current.count;
      } else {
        neighborhoodMap.set(neighborhood, { 
          total: pricePerSqm, 
          count: 1, 
          value: pricePerSqm 
        });
      }
    });

    return Array.from(neighborhoodMap.entries())
      .map(([name, { value, count }]) => ({
        name,
        count,
        avgPricePerSqMeter: Math.round(value)
      }))
      .filter(item => item.count >= 3)
      .sort((a, b) => b.count - a.count)
      .slice(0, 15);
  }, [properties]);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const percentage = ((payload[0].value / properties.length) * 100).toFixed(1);
      return (
        <div className="bg-white p-3 border rounded shadow-md">
          <p className="font-medium text-base">{label}</p>
          <p className="text-sm">{`${payload[0].value.toLocaleString('pt-BR')} imóveis (${percentage}%)`}</p>
          {payload[1] && payload[1].value > 0 && (
            <p className="text-sm font-medium">
              {dashboardType === 'venda' 
                ? `Média de R$${payload[1].value.toLocaleString('pt-BR')}/m²` 
                : `Aluguel médio de R$${payload[1].value.toLocaleString('pt-BR')}/m²`}
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  if (chartData.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <p className="text-muted-foreground">Dados insuficientes para exibir o gráfico</p>
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart
        data={chartData}
        margin={{ top: 20, right: 50, left: 20, bottom: 70 }}
        barGap={0}
      >
        <CartesianGrid strokeDasharray="3 3" stroke={TAILWIND_COLORS['neutral-light']} opacity={0.5} />
        <XAxis 
          dataKey="name" 
          angle={-45} 
          textAnchor="end" 
          height={70} 
          tick={{ fontSize: 12, fontWeight: 500, fill: TAILWIND_COLORS['neutral-dark'] }}
          interval={0}
        />
        <YAxis 
          yAxisId="left"
          orientation="left" 
          tickFormatter={(value) => value.toLocaleString('pt-BR')}
          label={{ 
            value: 'Número de imóveis', 
            angle: -90, 
            position: 'insideLeft', 
            style: { 
              textAnchor: 'middle', 
              fontSize: 13,
              fontWeight: 500,
              fill: TAILWIND_COLORS['blue-base'],
              paddingLeft: 10
            } 
          }}
          tick={{ fontSize: 12, fill: TAILWIND_COLORS['neutral-dark'] }}
        />
        <YAxis 
          yAxisId="right"
          orientation="right"
          tickFormatter={(value) => `R$${(value/1000).toLocaleString('pt-BR')}k`}
          label={{ 
            value: 'Preço/m²', 
            angle: 90, 
            position: 'insideRight', 
            style: { 
              textAnchor: 'middle', 
              fontSize: 13,
              fontWeight: 500,
              fill: TAILWIND_COLORS['highlight'],
              paddingRight: 10
            } 
          }}
          tick={{ fontSize: 12, fill: TAILWIND_COLORS['neutral-dark'] }}
        />
        <Tooltip content={<CustomTooltip />} />
        <Bar 
          yAxisId="left" 
          dataKey="count" 
          name="Imóveis" 
          fill={TAILWIND_COLORS['blue-base']}
          radius={[4, 4, 0, 0]} 
          maxBarSize={40}
        />
        <Line 
          yAxisId="right"
          type="monotone" 
          dataKey="avgPricePerSqMeter" 
          name='Preço/m²'
          stroke={TAILWIND_COLORS['highlight']}
          strokeWidth={2}
          dot={{ r: 4, fill: TAILWIND_COLORS['highlight'], strokeWidth: 1 }}
          activeDot={{ r: 6 }}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

export default NeighborhoodChart;
